import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DEFAULT_TEMPLATE_KEY, SECTIONS_LAYOUT_KEY } from '@capital-access/common/utils';
import { ReportTemplate, ReportType, TemplateLibraryService } from '@capital-access/reporting/common';
import { removeLocalStorage } from '@capital-access/reporting/common';
import { ItineraryReportLayout } from '../../itinerary/models/itinerary-report-sections';
import { DEFAULT_LAYOUT, ProfileReportLayout } from '../../profile/models/profile-report-sections';
import { saveReportLayoutSettings } from '../+state/report.actions';
import { getSavedLayout } from './section-settings.utils';

//  * Checks if the default template exists If not, dispatches actions to reset layout and remove localStorage.
export function getValidConfigurableLayout(
  store: Store,
  templateService: TemplateLibraryService,
  profileType: string
): Observable<ProfileReportLayout> {
  const layout = getSavedLayout(profileType, DEFAULT_LAYOUT) ?? DEFAULT_LAYOUT;
  if (layout === ProfileReportLayout.Configurable) {
    const defaultTemplateId = Number(localStorage.getItem(`${profileType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()) ?? 0);
    if (defaultTemplateId > 0) {
      return templateService.getTemplates().pipe(
        map(templates => {
          if (!templates.some(t => t.templateId === defaultTemplateId)) {
            store.dispatch(
              saveReportLayoutSettings({
                key: `${profileType}_${SECTIONS_LAYOUT_KEY}`.toUpperCase(),
                layout: DEFAULT_LAYOUT
              })
            );
            store.dispatch(
              removeLocalStorage({
                keys: [`${profileType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()]
              })
            );
            return DEFAULT_LAYOUT as ProfileReportLayout;
          }
          return layout;
        })
      );
    }
  }
  return of(layout);
}

const ITINERARY_LAYOUT_KEY = `${ReportType.Itinerary}_${SECTIONS_LAYOUT_KEY}`.toUpperCase();
const ITINERARY_TEMPLATE_KEY = `${ReportType.Itinerary}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase();

// Checks if the default itinerary template exists. If not, dispatches actions to reset layout and remove localStorage.
export function getValidItineraryLayout(
  store: Store,
  templateService: TemplateLibraryService
): Observable<{ layout: ItineraryReportLayout; templates?: ReportTemplate[] }> {
  const savedLayout = localStorage.getItem(ITINERARY_LAYOUT_KEY) as ItineraryReportLayout | null;
  const layout = savedLayout || ItineraryReportLayout.Standard;

  if (layout === ItineraryReportLayout.Configurable) {
    const defaultTemplateId = Number(localStorage.getItem(ITINERARY_TEMPLATE_KEY) ?? 0);
    if (defaultTemplateId > 0) {
      return templateService.getTemplates().pipe(
        map(templates => {
          const templateExists = templates.some(
            t => t.templateId === defaultTemplateId && !t.caTemplateInd && t.reportType === ReportType.Itinerary
          );
          if (!templateExists) {
            store.dispatch(
              saveReportLayoutSettings({
                key: ITINERARY_LAYOUT_KEY,
                layout: ItineraryReportLayout.Standard
              })
            );
            store.dispatch(removeLocalStorage({ keys: [ITINERARY_TEMPLATE_KEY] }));
            return { layout: ItineraryReportLayout.Standard };
          }
          return { layout, templates };
        })
      );
    }
  }
  return of({ layout });
}
